/**
 * Client-side CSV export. Builds a spreadsheet-safe CSV from the rows already
 * loaded in the page and triggers a browser download — no backend endpoint
 * needed (exports exactly what the user sees). Excel-friendly: CRLF + UTF-8 BOM.
 */
type Cell = string | number | null | undefined;

/**
 * Escape one cell. Beyond RFC-4180 quoting, neutralize CSV formula injection
 * (CWE-1236): a cell a spreadsheet would treat as a formula (leading =, +, -,
 * @, tab, or CR — e.g. a business named `=cmd|...`) gets a leading apostrophe
 * and is always quoted, so it renders as literal text instead of executing.
 */
function esc(v: Cell): string {
  let s = v == null ? '' : String(v).replace(/\0/g, '');
  const dangerous = /^[=+\-@\t\r]/.test(s);
  if (dangerous) s = `'${s}`;
  return dangerous || /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(filename: string, headers: string[], rows: Cell[][]): void {
  const body = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
